// SCQA landing page content — 7 SEO pages, EN/FR.

const page = (en, fr) => ({ en, fr });

export const LANDINGS = {
  "private-assistant-france": page(
    {
      seoTitle: "Personal Assistant in France for International Clients | Book & Smile",
      seoDesc: "Retain a personal assistant in France: one trusted bilingual person handling French administration, household, healthcare and daily life — on monthly retainer.",
      eyebrow: "Private Assistant · France",
      h1a: "A private assistant in France.",
      h1b: "One person. Everything handled.",
      answer: "Retain one trusted, bilingual private assistant in France. Your administration, healthcare, household, schools and daily errands — owned end to end by a single person, on a monthly retainer, under strict confidentiality.",
      s: "Your life in France runs on institutions: the préfecture, CPAM, banks, insurers, schools, utility providers. Each speaks French, works its own hours and expects local presence.",
      c: "Handled alone, this consumes days every month and stalls at every language barrier. Handled by scattered providers, it means chasing five inboxes and repeating your story each time.",
      q: "What would change if one trusted person simply handled all of it?",
      deliverablesTitle: "What your assistant handles",
      deliverables: [
        "Personal administration: prefecture, CPAM, taxes correspondence, renewals",
        "Healthcare: doctors, specialists, appointments, reimbursements",
        "Household: contractors, utilities, insurance, deliveries, staff coordination",
        "Schools & family: enrolments, communication, activities",
        "Errands & presence: on-site representation anywhere in Île-de-France",
        "Anything undefined: you ask, we scope it, we handle it",
      ],
      forTitle: "Who retains us",
      forWho: [
        "International residents who refuse to lose days to French administration",
        "Families settling into or established in Paris and the Île-de-France",
        "Non-residents managing a French home, property or affairs from abroad",
      ],
      faqs: [
        { q: "Is the assistant dedicated to me?", a: "Yes. One named, bilingual interlocutor owns your file. You never repeat yourself." },
        { q: "Can you represent me in person?", a: "Yes — at appointments, deliveries, inspections and administrative offices across Paris and the Île-de-France, with written authority where required." },
        { q: "What does it cost?", a: "Terms are defined in a private consultation, according to scope and level of service. No hourly billing; third-party costs at cost, pre-approved." },
      ],
      waMsg: "Hello, I am interested in a Private Assistant in France.",
    },
    {
      seoTitle: "Assistant personnel en France pour clients internationaux | Book & Smile",
      seoDesc: "Retenez un assistant personnel en France : une personne de confiance bilingue qui gère administration, maison, santé et quotidien — au forfait mensuel.",
      eyebrow: "Assistant privé · France",
      h1a: "Un assistant privé en France.",
      h1b: "Une personne. Tout est géré.",
      answer: "Retenez un assistant privé bilingue et de confiance en France. Votre administration, votre santé, votre maison, les écoles et les courses du quotidien — portés de bout en bout par une seule personne, au forfait mensuel, sous stricte confidentialité.",
      s: "Votre vie en France repose sur des institutions : préfecture, CPAM, banques, assureurs, écoles, fournisseurs. Chacune parle français, suit ses propres horaires et exige une présence locale.",
      c: "Seul, cela consomme des journées chaque mois et bute sur chaque barrière de langue. Avec des prestataires dispersés, cela signifie relancer cinq boîtes mail et répéter votre histoire à chaque fois.",
      q: "Que changerait une seule personne de confiance qui gère tout ?",
      deliverablesTitle: "Ce que votre assistant gère",
      deliverables: [
        "Administration personnelle : préfecture, CPAM, correspondance fiscale, renouvellements",
        "Santé : médecins, spécialistes, rendez-vous, remboursements",
        "Maison : artisans, énergie, assurances, livraisons, coordination du personnel",
        "Écoles & famille : inscriptions, communication, activités",
        "Courses & présence : représentation sur place partout en Île-de-France",
        "Tout le reste : vous demandez, nous cadrons, nous gérons",
      ],
      forTitle: "Qui nous retient",
      forWho: [
        "Résidents internationaux qui refusent de perdre des journées dans l'administration française",
        "Familles qui s'installent ou sont installées à Paris et en Île-de-France",
        "Non-résidents gérant une maison, un bien ou des affaires en France depuis l'étranger",
      ],
      faqs: [
        { q: "L'assistant m'est-il dédié ?", a: "Oui. Un interlocuteur bilingue nommé porte votre dossier. Vous ne vous répétez jamais." },
        { q: "Pouvez-vous me représenter en personne ?", a: "Oui — rendez-vous, livraisons, états des lieux et guichets administratifs dans tout Paris et l'Île-de-France, avec mandat écrit si nécessaire." },
        { q: "Quel est le coût ?", a: "Les conditions sont définies en consultation privée, selon le périmètre et le niveau de service. Pas de facturation horaire ; coûts tiers au réel, pré-approuvés." },
      ],
      waMsg: "Bonjour, je suis intéressé(e) par un assistant privé en France.",
    }
  ),

  "private-assistant-paris": page(
    {
      seoTitle: "Personal Assistant in Paris — Bilingual, On Retainer | Book & Smile",
      seoDesc: "A bilingual personal assistant in Paris, on monthly retainer. Administration, household, appointments and daily life — one trusted person.",
      eyebrow: "Private Assistant · Paris",
      h1a: "Your private assistant",
      h1b: "in Paris.",
      answer: "One bilingual private assistant in Paris, on retainer — physically present where you need them, handling your administration, home, appointments and daily life with complete discretion.",
      s: "Paris rewards presence. The bank adviser who moves your file, the school director who returns calls, the artisan who actually shows up — all respond to someone standing in front of them, speaking French.",
      c: "From a phone abroad, or between two meetings, that presence is impossible. Remote-only services stop precisely where Paris begins: in person.",
      q: "Who stands in for you, in Paris, today?",
      deliverablesTitle: "Handled in person, in Paris",
      deliverables: [
        "On-site presence: banks, administrations, schools, ateliers",
        "Home oversight: contractors supervised, deliveries received, inventories checked",
        "Appointments: booked, prepared, attended with you or for you",
        "Daily life: pharmacies, tailors, framers, florists — the right addresses",
        "Arrivals & departures: airport coordination, home opened and ready",
        "Written reporting: every task confirmed, with photos where useful",
      ],
      forTitle: "Who retains us",
      forWho: [
        "Residents of the western arrondissements and Neuilly who want one number to call",
        "International owners of Paris pieds-à-terre living abroad",
        "Executives posted to Paris whose time is not for queues",
      ],
      faqs: [
        { q: "Which areas do you cover?", a: "All of Paris, with particular depth in the 1st, 7th, 8th, 16th and 17th arrondissements, Neuilly-sur-Seine and the western suburbs." },
        { q: "Can you hold keys to my home?", a: "Yes, under a signed key-holding protocol with logged access and written reporting after each visit." },
        { q: "Is this available without residency in Paris?", a: "Yes. Many members live abroad; the desk is their standing presence in Paris." },
      ],
      waMsg: "Hello, I am interested in a Private Assistant in Paris.",
    },
    {
      seoTitle: "Assistant personnel à Paris — Bilingue, au forfait | Book & Smile",
      seoDesc: "Un assistant personnel bilingue à Paris, au forfait mensuel. Administration, maison, rendez-vous et quotidien — gérés par une seule personne de confiance.",
      eyebrow: "Assistant privé · Paris",
      h1a: "Votre assistant privé",
      h1b: "à Paris.",
      answer: "Un assistant privé bilingue à Paris, au forfait — physiquement présent là où vous en avez besoin, gérant votre administration, votre maison, vos rendez-vous et votre quotidien avec une discrétion totale.",
      s: "Paris récompense la présence. Le conseiller bancaire qui fait avancer votre dossier, la directrice d'école qui rappelle, l'artisan qui vient vraiment — tous répondent à quelqu'un qui est là, et qui parle français.",
      c: "Depuis un téléphone à l'étranger, ou entre deux réunions, cette présence est impossible. Les services à distance s'arrêtent exactement là où Paris commence : en personne.",
      q: "Qui vous représente, à Paris, aujourd'hui ?",
      deliverablesTitle: "Géré en personne, à Paris",
      deliverables: [
        "Présence sur place : banques, administrations, écoles, ateliers",
        "Supervision du domicile : artisans encadrés, livraisons réceptionnées, inventaires vérifiés",
        "Rendez-vous : pris, préparés, honorés avec vous ou pour vous",
        "Quotidien : pharmacies, tailleurs, encadreurs, fleuristes — les bonnes adresses",
        "Arrivées & départs : coordination aéroport, maison ouverte et prête",
        "Reporting écrit : chaque tâche confirmée, photos à l'appui si utile",
      ],
      forTitle: "Qui nous retient",
      forWho: [
        "Résidents de l'ouest parisien et de Neuilly qui veulent un seul numéro à appeler",
        "Propriétaires internationaux de pieds-à-terre parisiens vivant à l'étranger",
        "Dirigeants en poste à Paris dont le temps n'est pas fait pour les files d'attente",
      ],
      faqs: [
        { q: "Quelles zones couvrez-vous ?", a: "Tout Paris, avec une connaissance particulière des 1er, 7e, 8e, 16e et 17e arrondissements, de Neuilly-sur-Seine et de l'ouest parisien." },
        { q: "Pouvez-vous détenir mes clés ?", a: "Oui, dans le cadre d'un protocole signé de détention de clés, avec accès consignés et reporting écrit après chaque visite." },
        { q: "Est-ce accessible sans résider à Paris ?", a: "Oui. Beaucoup de nos membres vivent à l'étranger ; le desk est leur présence permanente à Paris." },
      ],
      waMsg: "Bonjour, je suis intéressé(e) par un assistant privé à Paris.",
    }
  ),

  "executive-support-france": page(
    {
      seoTitle: "Executive Support in France — Bilingual, On Retainer | Book & Smile",
      seoDesc: "Bilingual executive support in France: calendar, travel, correspondence, meeting preparation and follow-through for international leaders. By Book & Smile.",
      eyebrow: "Executive Support · France",
      h1a: "Executive support,",
      h1b: "built for France.",
      answer: "Retain bilingual executive support in France: your calendar, travel, French correspondence, meeting preparation and follow-ups — handled by one person who operates natively in both your world and the French one.",
      s: "You lead internationally. Your existing assistant is excellent — in your language, in your time zone, in your systems.",
      c: "France is none of those. French counterparties expect French letters, French phone calls and French formality. Every exchange your team cannot handle natively becomes your personal backlog.",
      q: "What if your executive support simply extended into France?",
      deliverablesTitle: "What the desk handles",
      deliverables: [
        "Calendar & scheduling with French counterparties, in French",
        "Business correspondence: drafted, sent, chased to conclusion",
        "Travel: flights, cars, hotels, restaurants — confirmed in writing",
        "Meeting preparation: agendas, briefings, follow-up minutes",
        "Institutional liaison: banks, notaries, administrations, chambers",
        "Coordination with your existing EA and team, seamlessly",
      ],
      forTitle: "Who retains us",
      forWho: [
        "International executives with recurring business in France",
        "Founders and investors operating French entities from abroad",
        "Family principals whose French affairs outgrew email",
      ],
      faqs: [
        { q: "Do you replace my current assistant?", a: "No. We extend them. Your EA keeps the global picture; the desk executes everything that requires French language, presence or process." },
        { q: "How do you handle confidentiality?", a: "NDA by default, single-person file handling, and no reference to members in any context." },
        { q: "What level do I need?", a: "Executive membership covers this scope; complex corporate needs move to Private Office. Terms are defined in a private consultation." },
      ],
      waMsg: "Hello, I am interested in Executive Support in France.",
    },
    {
      seoTitle: "Support exécutif en France — Bilingue, au forfait | Book & Smile",
      seoDesc: "Support exécutif bilingue en France : agenda, déplacements, correspondance, préparation de réunions et suivi pour dirigeants internationaux. Par Book & Smile.",
      eyebrow: "Support exécutif · France",
      h1a: "Un support exécutif,",
      h1b: "pensé pour la France.",
      answer: "Retenez un support exécutif bilingue en France : votre agenda, vos déplacements, votre correspondance française, la préparation de vos réunions et les relances — portés par une personne qui opère nativement dans votre monde et dans le monde français.",
      s: "Vous dirigez à l'international. Votre assistante actuelle est excellente — dans votre langue, votre fuseau, vos systèmes.",
      c: "La France n'est rien de tout cela. Les interlocuteurs français attendent des courriers français, des appels français, une formalité française. Chaque échange que votre équipe ne peut traiter nativement devient votre charge personnelle.",
      q: "Et si votre support exécutif s'étendait simplement à la France ?",
      deliverablesTitle: "Ce que le desk gère",
      deliverables: [
        "Agenda & planification avec les interlocuteurs français, en français",
        "Correspondance professionnelle : rédigée, envoyée, relancée jusqu'à conclusion",
        "Déplacements : vols, voitures, hôtels, restaurants — confirmés par écrit",
        "Préparation de réunions : ordres du jour, briefings, comptes rendus",
        "Liaison institutionnelle : banques, notaires, administrations, chambres",
        "Coordination fluide avec votre assistante et votre équipe actuelles",
      ],
      forTitle: "Qui nous retient",
      forWho: [
        "Dirigeants internationaux avec des affaires récurrentes en France",
        "Fondateurs et investisseurs pilotant des entités françaises depuis l'étranger",
        "Principals dont les affaires françaises ont dépassé le stade de l'email",
      ],
      faqs: [
        { q: "Remplacez-vous mon assistante actuelle ?", a: "Non. Nous la prolongeons. Votre assistante garde la vue globale ; le desk exécute tout ce qui exige la langue, la présence ou les procédures françaises." },
        { q: "Comment gérez-vous la confidentialité ?", a: "NDA par défaut, dossier traité par une seule personne, et aucune référence à nos membres, dans aucun contexte." },
        { q: "Quel niveau me faut-il ?", a: "L'adhésion Executive couvre ce périmètre ; les besoins corporate complexes relèvent du Private Office. Conditions définies en consultation privée." },
      ],
      waMsg: "Bonjour, je suis intéressé(e) par un support exécutif en France.",
    }
  ),

  "executive-assistant-france": page(
    {
      seoTitle: "Executive Assistant in France, On Retainer | Book & Smile",
      seoDesc: "An executive assistant in France without hiring: bilingual, on monthly retainer, NDA by default. Calendar, correspondence, travel and follow-through.",
      eyebrow: "Executive Assistant · France",
      h1a: "An executive assistant in France —",
      h1b: "without the hire.",
      answer: "Get a senior, bilingual executive assistant in France without recruiting one: on retainer from day one, NDA by default, no employment contract, no payroll, no notice periods.",
      s: "Hiring an EA in France means French employment law: contracts, payroll, social charges, and a commitment measured in years.",
      c: "For a presence you need days per month — not full time — a French hire is the wrong instrument. Freelancer platforms, at the other extreme, offer no continuity and no accountability.",
      q: "What sits between a full-time hire and a stranger from a platform?",
      deliverablesTitle: "What you get",
      deliverables: [
        "A named senior assistant, bilingual French–English",
        "Operational from day one — no recruitment, no onboarding project",
        "Calendar, correspondence, travel and meeting support",
        "Follow-through: every open item chased to written conclusion",
        "Elastic capacity: quiet months cost the same, heavy months are absorbed",
        "One monthly invoice. No payroll, no social charges, no severance",
      ],
      forTitle: "Who retains us",
      forWho: [
        "Executives who need French-side support days per month, not full time",
        "Companies testing the French market before building a local team",
        "Principals replacing a departed assistant without a hiring cycle",
      ],
      faqs: [
        { q: "How is this different from hiring?", a: "No employment relationship: a professional services retainer, cancellable after the three-month minimum, with continuity guaranteed by the desk." },
        { q: "Can you work inside our tools?", a: "Yes — your calendar, inbox conventions and document systems, under your access policies." },
        { q: "What does it cost?", a: "Terms are defined in a private consultation. Compare them with the full cost of a part-time bilingual EA in Paris." },
      ],
      waMsg: "Hello, I am interested in an Executive Assistant in France.",
    },
    {
      seoTitle: "Executive assistant en France, au forfait | Book & Smile",
      seoDesc: "Une assistante de direction en France sans recruter : bilingue, au forfait mensuel, NDA par défaut. Agenda, correspondance, déplacements et suivi.",
      eyebrow: "Executive assistant · France",
      h1a: "Une assistante de direction en France —",
      h1b: "sans recrutement.",
      answer: "Disposez d'une assistante de direction senior et bilingue en France sans la recruter : au forfait dès le premier jour, NDA par défaut, sans contrat de travail, sans paie, sans préavis.",
      s: "Recruter une assistante en France, c'est le droit du travail français : contrats, paie, charges sociales, et un engagement qui se mesure en années.",
      c: "Pour une présence dont vous avez besoin quelques jours par mois — pas à temps plein — l'embauche française est le mauvais instrument. Les plateformes de freelances, à l'autre extrême, n'offrent ni continuité ni responsabilité.",
      q: "Qu'existe-t-il entre l'embauche à temps plein et un inconnu d'une plateforme ?",
      deliverablesTitle: "Ce que vous obtenez",
      deliverables: [
        "Une assistante senior nommée, bilingue français–anglais",
        "Opérationnelle dès le premier jour — sans recrutement ni onboarding",
        "Agenda, correspondance, déplacements et support de réunions",
        "Suivi : chaque sujet ouvert relancé jusqu'à conclusion écrite",
        "Capacité élastique : les mois calmes coûtent pareil, les mois chargés sont absorbés",
        "Une facture mensuelle. Ni paie, ni charges, ni indemnités",
      ],
      forTitle: "Qui nous retient",
      forWho: [
        "Dirigeants qui ont besoin d'un support côté France quelques jours par mois",
        "Sociétés qui testent le marché français avant de constituer une équipe locale",
        "Principals qui remplacent une assistante partie, sans cycle de recrutement",
      ],
      faqs: [
        { q: "En quoi est-ce différent d'une embauche ?", a: "Aucune relation de travail : un forfait de services professionnels, résiliable après le minimum de trois mois, avec une continuité garantie par le desk." },
        { q: "Pouvez-vous travailler dans nos outils ?", a: "Oui — votre agenda, vos conventions d'email et vos systèmes documentaires, selon vos politiques d'accès." },
        { q: "Quel est le coût ?", a: "Les conditions sont définies en consultation privée. À comparer au coût complet d'une assistante bilingue à temps partiel à Paris." },
      ],
      waMsg: "Bonjour, je suis intéressé(e) par une executive assistant en France.",
    }
  ),

  "business-support-france": page(
    {
      seoTitle: "Business Support in France for Foreign Firms | Book & Smile",
      seoDesc: "Business support in France for foreign companies and founders: formalities, banking, URSSAF and tax calendars, liaison with accountants and lawyers.",
      eyebrow: "Business in France · Corporate",
      h1a: "Your business in France",
      h1b: "needs a desk in France.",
      answer: "Retain a corporate desk in France: one bilingual interlocutor who coordinates your company's French formalities, banking relationships, administrative calendars and licensed advisors — so your entity runs without you standing over it.",
      s: "Your French entity exists on paper: a Kbis, a bank account, an accountant somewhere in Paris.",
      c: "In practice, it generates a constant stream of French-language obligations — URSSAF notices, bank compliance requests, registered letters — each with a deadline and a penalty. From abroad, they arrive late, translated poorly, or not at all.",
      q: "Who owns your French administrative calendar?",
      deliverablesTitle: "What the desk coordinates",
      deliverables: [
        "Company formalities: registrations, changes, filings — prepared and tracked",
        "Banking: account relationships, compliance requests, signatory logistics",
        "Administrative calendar: URSSAF, tax and legal deadlines, monitored",
        "Registered mail: received, translated, actioned same week",
        "Advisor liaison: your accountants, lawyers and notaries, coordinated",
        "Monthly written summary of your entity's French administrative state",
      ],
      forTitle: "Who retains us",
      forWho: [
        "Foreign companies with a French subsidiary and no local admin staff",
        "Founders who incorporated in France and live elsewhere",
        "Investors holding French assets through French structures",
      ],
      faqs: [
        { q: "Are you accountants or lawyers?", a: "No — and we do not replace them. We coordinate the licensed professionals, hold the calendar and guarantee follow-through." },
        { q: "Can you receive our mail?", a: "We monitor, collect and process correspondence under a written mandate; formal domiciliation runs through licensed providers we coordinate." },
        { q: "Which membership covers this?", a: "Private Office membership for full corporate coverage; lighter needs fit Executive. Terms defined in a private consultation." },
      ],
      waMsg: "Hello, I am interested in Business Support in France.",
    },
    {
      seoTitle: "Support business en France — Votre desk corporate | Book & Smile",
      seoDesc: "Support business en France pour sociétés étrangères et fondateurs : formalités, banque, calendriers URSSAF et fiscaux, liaison experts-comptables et avocats.",
      eyebrow: "Business en France · Corporate",
      h1a: "Votre activité en France",
      h1b: "a besoin d'un desk en France.",
      answer: "Retenez un desk corporate en France : un interlocuteur bilingue qui coordonne les formalités françaises de votre société, vos relations bancaires, vos calendriers administratifs et vos conseils agréés — pour que votre entité tourne sans que vous la surveilliez.",
      s: "Votre entité française existe sur le papier : un Kbis, un compte bancaire, un expert-comptable quelque part à Paris.",
      c: "En pratique, elle génère un flux constant d'obligations en français — avis URSSAF, demandes de conformité bancaire, courriers recommandés — chacune avec une échéance et une pénalité. Depuis l'étranger, elles arrivent tard, mal traduites, ou pas du tout.",
      q: "Qui détient votre calendrier administratif français ?",
      deliverablesTitle: "Ce que le desk coordonne",
      deliverables: [
        "Formalités : immatriculations, modifications, dépôts — préparés et suivis",
        "Banque : relations de compte, demandes de conformité, logistique des signataires",
        "Calendrier administratif : échéances URSSAF, fiscales et légales, surveillées",
        "Courriers recommandés : reçus, traduits, traités dans la semaine",
        "Liaison conseils : vos experts-comptables, avocats et notaires, coordonnés",
        "Synthèse écrite mensuelle de l'état administratif français de votre entité",
      ],
      forTitle: "Qui nous retient",
      forWho: [
        "Sociétés étrangères avec une filiale française sans équipe administrative locale",
        "Fondateurs immatriculés en France et vivant ailleurs",
        "Investisseurs détenant des actifs français via des structures françaises",
      ],
      faqs: [
        { q: "Êtes-vous experts-comptables ou avocats ?", a: "Non — et nous ne les remplaçons pas. Nous coordonnons les professionnels agréés, tenons le calendrier et garantissons le suivi." },
        { q: "Pouvez-vous recevoir notre courrier ?", a: "Nous surveillons, collectons et traitons la correspondance sous mandat écrit ; la domiciliation formelle passe par des prestataires agréés que nous coordonnons." },
        { q: "Quelle adhésion couvre ce périmètre ?", a: "Private Office pour une couverture corporate complète ; les besoins plus légers relèvent d'Executive. Conditions définies en consultation privée." },
      ],
      waMsg: "Bonjour, je suis intéressé(e) par un support business en France.",
    }
  ),

  "vip-concierge-paris": page(
    {
      seoTitle: "VIP Concierge in Paris for International Clients | Book & Smile",
      seoDesc: "A VIP concierge in Paris for international clients: restaurants, hotels, private experiences, chauffeurs and cultural access — arranged discreetly.",
      eyebrow: "VIP & Lifestyle · Paris",
      h1a: "Paris, arranged",
      h1b: "quietly.",
      answer: "Retain a VIP concierge in Paris who arranges the city around you — tables, suites, private experiences, chauffeurs, gifting and cultural access — discreetly, and always confirmed in writing.",
      s: "The Paris you want operates on relationships: the table that does not appear on booking platforms, the atelier visit that is not offered publicly, the suite released on a phone call.",
      c: "Hotel concierges serve their guests, apps serve their inventory, and neither knows you. Access without relationship is a waiting list.",
      q: "Who calls Paris on your behalf?",
      deliverablesTitle: "What we arrange",
      deliverables: [
        "Restaurants: the right table, at the right hour, under your preferences",
        "Hotels & suites: negotiated, confirmed, arrival-checked",
        "Private experiences: ateliers, cellars, after-hours visits",
        "Chauffeurs & transfers: vetted drivers, airport to door",
        "Gifting: sourced, wrapped, delivered with your card",
        "Cultural agenda: openings, premieres, invitations you would not see",
      ],
      forTitle: "Who retains us",
      forWho: [
        "International members visiting Paris regularly",
        "Residents who want a lifestyle desk, not another app",
        "Executives hosting guests who must be impressed quietly",
      ],
      faqs: [
        { q: "Are you available on short notice?", a: "Members reach the desk directly; same-day requests are the norm, not the exception." },
        { q: "Do you charge per request?", a: "No. Concierge access is part of membership; third-party costs are at cost, pre-approved." },
        { q: "How discreet is discreet?", a: "Bookings made in the desk's name where you prefer, NDA by default, no client names spoken anywhere." },
      ],
      waMsg: "Hello, I am interested in the VIP Concierge in Paris.",
    },
    {
      seoTitle: "Conciergerie VIP à Paris pour clients internationaux | Book & Smile",
      seoDesc: "Une conciergerie VIP à Paris pour clients internationaux : restaurants, hôtels, expériences privées, chauffeurs et accès culturel — organisés discrètement.",
      eyebrow: "VIP & Art de vivre · Paris",
      h1a: "Paris, organisé",
      h1b: "discrètement.",
      answer: "Retenez une conciergerie VIP à Paris qui organise la ville autour de vous — tables, suites, expériences privées, chauffeurs, cadeaux et accès culturel — discrètement, et toujours confirmé par écrit.",
      s: "Le Paris que vous voulez fonctionne aux relations : la table absente des plateformes, la visite d'atelier jamais proposée publiquement, la suite libérée sur un coup de fil.",
      c: "Les concierges d'hôtel servent leurs clients, les applications servent leur inventaire, et aucun ne vous connaît. Un accès sans relation, c'est une liste d'attente.",
      q: "Qui appelle Paris en votre nom ?",
      deliverablesTitle: "Ce que nous organisons",
      deliverables: [
        "Restaurants : la bonne table, à la bonne heure, selon vos préférences",
        "Hôtels & suites : négociés, confirmés, vérifiés à l'arrivée",
        "Expériences privées : ateliers, caves, visites hors horaires",
        "Chauffeurs & transferts : chauffeurs vérifiés, de l'aéroport à la porte",
        "Cadeaux : sourcés, emballés, livrés avec votre carte",
        "Agenda culturel : vernissages, premières, invitations que vous ne verriez pas",
      ],
      forTitle: "Qui nous retient",
      forWho: [
        "Membres internationaux visitant Paris régulièrement",
        "Résidents qui veulent un desk art de vivre, pas une application de plus",
        "Dirigeants recevant des invités qu'il faut impressionner discrètement",
      ],
      faqs: [
        { q: "Êtes-vous disponibles en urgence ?", a: "Les membres joignent le desk directement ; les demandes du jour même sont la norme, pas l'exception." },
        { q: "Facturez-vous à la demande ?", a: "Non. L'accès conciergerie fait partie de l'adhésion ; les coûts tiers sont au réel, pré-approuvés." },
        { q: "Discret à quel point ?", a: "Réservations au nom du desk si vous préférez, NDA par défaut, aucun nom de client prononcé, nulle part." },
      ],
      waMsg: "Bonjour, je suis intéressé(e) par la conciergerie VIP à Paris.",
    }
  ),

  "personal-assistant-for-international-clients": page(
    {
      seoTitle: "Personal Assistant for International Clients | Book & Smile",
      seoDesc: "A personal assistant for international clients in France: bilingual, discreet, on retainer. Administration, private concierge, household, lifestyle.",
      eyebrow: "Personal Assistant · International Clients",
      h1a: "Built for clients",
      h1b: "the system wasn't built for.",
      answer: "Retain a personal assistant designed specifically for international clients in France: bilingual by default, fluent in the gap between your world and the French system, and accountable in writing for everything you delegate.",
      s: "France works beautifully — for people raised inside its codes. Its administration, etiquette and institutions assume a native's reflexes.",
      c: "International clients are served last and understood least. Standard assistants know the tasks; they do not know the translation layer — of language, of formality, of expectation — where everything actually fails.",
      q: "Shouldn't your assistant be built for that layer?",
      deliverablesTitle: "What we handle across the gap",
      deliverables: [
        "Two-language operations: every call, letter and meeting, natively handled",
        "Cultural navigation: the formality French institutions expect, applied for you",
        "Time-zone cover: instructions overnight, resolved by your morning",
        "Cross-border coordination: your advisors abroad, aligned with France",
        "Family support: spouses, children, staff — everyone has the same desk",
        "Continuity: one file, one person, however long you stay",
      ],
      forTitle: "Who retains us",
      forWho: [
        "Non-French speakers living in or moving to France",
        "Global families with French interests and no French infrastructure",
        "International executives, diplomats and investors between countries",
      ],
      faqs: [
        { q: "Which languages do you work in?", a: "Native French and fluent English, with vetted interpreters coordinated for other languages when needed." },
        { q: "Do you serve clients not yet in France?", a: "Yes. Many engagements begin weeks before arrival — the desk prepares the ground so day one is operational." },
        { q: "How do we start?", a: "A private introduction, a written proposal, a three-month minimum membership. Most members stay by the year." },
      ],
      waMsg: "Hello, I am an international client interested in a Personal Assistant in France.",
    },
    {
      seoTitle: "Assistant personnel, clients internationaux | Book & Smile",
      seoDesc: "Un assistant personnel pour clients internationaux en France : bilingue, discret, au forfait. Administration, conciergerie privée, maison, lifestyle.",
      eyebrow: "Assistant personnel · Clients internationaux",
      h1a: "Conçu pour les clients",
      h1b: "que le système ignore.",
      answer: "Retenez un assistant personnel conçu spécifiquement pour les clients internationaux en France : bilingue par défaut, à l'aise dans l'écart entre votre monde et le système français, et responsable par écrit de tout ce que vous déléguez.",
      s: "La France fonctionne magnifiquement — pour ceux qui ont grandi dans ses codes. Son administration, son étiquette et ses institutions supposent des réflexes de natif.",
      c: "Les clients internationaux sont servis en dernier et compris le moins. Les assistants classiques connaissent les tâches ; ils ignorent la couche de traduction — de langue, de formalité, d'attentes — là où tout échoue réellement.",
      q: "Votre assistant ne devrait-il pas être conçu pour cette couche ?",
      deliverablesTitle: "Ce que nous gérons dans cet écart",
      deliverables: [
        "Opérations en deux langues : chaque appel, courrier et rendez-vous, traité nativement",
        "Navigation culturelle : la formalité qu'attendent les institutions françaises, appliquée pour vous",
        "Couverture des fuseaux : instructions la nuit, résolues à votre réveil",
        "Coordination transfrontalière : vos conseils à l'étranger, alignés avec la France",
        "Support familial : conjoints, enfants, personnel — tous ont le même desk",
        "Continuité : un dossier, une personne, aussi longtemps que vous restez",
      ],
      forTitle: "Qui nous retient",
      forWho: [
        "Non-francophones vivant en France ou s'y installant",
        "Familles globales avec des intérêts français sans infrastructure française",
        "Dirigeants internationaux, diplomates et investisseurs entre plusieurs pays",
      ],
      faqs: [
        { q: "Dans quelles langues travaillez-vous ?", a: "Français natif et anglais courant, avec des interprètes vérifiés coordonnés pour les autres langues si besoin." },
        { q: "Servez-vous des clients pas encore en France ?", a: "Oui. Beaucoup d'engagements commencent des semaines avant l'arrivée — le desk prépare le terrain pour que le jour un soit opérationnel." },
        { q: "Comment commence-t-on ?", a: "Une introduction privée, une proposition écrite, une adhésion de trois mois minimum. La plupart des membres restent à l'année." },
      ],
      waMsg: "Bonjour, je suis un client international intéressé par un assistant personnel en France.",
    }
  ),
};
